import { Injectable, resource, signal } from "@angular/core";
import {
  ProductConfigurationDto,
  ProductConfigurationPriceDto,
  ProductConfigurationPriceOverviewDto,
  RequestAddOnDto,
  SubscriptionAddOnPriceOverviewDto,
  SubscriptionQuoteDto
} from "@libs/shares/dto/createSubscriptionAddOn.dto";
import { purchasableProducts } from "@libs/shares/models/models";
import { BillingIntervalInMonths } from "@libs/shares/models/subscription.model";
import { environment } from "apps/portal-webapp/src/environments/environment";

@Injectable({
  providedIn: "root"
})
export class PlanConfigurationPriceService {
  allProducts = purchasableProducts.map((product) => ({
    product,
    callLimit: 100000,
    rps: 1
  }));
  _selectedProducts = signal<ProductConfigurationDto[]>([]);
  billingInterval = signal<BillingIntervalInMonths>(BillingIntervalInMonths.One);

  _addOnConfig = signal<RequestAddOnDto>({
    apiKeyCount: 1,
    applicationCount: 1,
    userCount: 1
  });

  get selectedProducts() {
    return this._selectedProducts.asReadonly();
  }

  get selectedAddOns() {
    return this._addOnConfig.asReadonly();
  }

  updateSelectedProducts(productConfig: ProductConfigurationDto) {
    // update or add product configuration
    this._selectedProducts.update((products) => {
      const existingProduct = products.find((productItem) => productItem.product === productConfig.product);
      if (existingProduct) {
        return products.map((productItem) =>
          productItem.product === productConfig.product ? { ...productItem, ...productConfig } : productItem
        );
      }
      return [...products, productConfig];
    });
  }

  updateAddOnConfig(addOnConfig: RequestAddOnDto) {
    this._addOnConfig.update(() => {
      return {
        apiKeyCount: addOnConfig.apiKeyCount,
        applicationCount: addOnConfig.applicationCount,
        userCount: addOnConfig.userCount
      };
    });
  }

  deselectProduct(productConfig: ProductConfigurationDto) {
    this._selectedProducts.update((products) =>
      products.filter((productItem) => productItem.product !== productConfig.product)
    );
  }

  deselectAll() {
    this._selectedProducts.set([]);
  }

  clearAll() {
    this.deselectAll();
    this._addOnConfig.set({
      apiKeyCount: 1,
      applicationCount: 1,
      userCount: 1
    });
    this.billingInterval.set(BillingIntervalInMonths.One);
  }

  getAllProductConfigPrice = resource<
    ProductConfigurationPriceDto[],
    | {
        productConfig: ProductConfigurationDto[];
        billingInterval: BillingIntervalInMonths;
      }
    | undefined
  >({
    request: () => {
      const config = this.allProducts;
      const interval = BillingIntervalInMonths.One;
      if (config.length === 0 || interval === undefined) {
        return undefined;
      }
      return {
        productConfig: config,
        billingInterval: interval
      };
    },
    loader: async ({ request, abortSignal }) => {
      const searchParams = new URLSearchParams({
        productConfig: JSON.stringify(request?.productConfig),
        billingInterval: request?.billingInterval?.toString() ?? ""
      });

      const data = await fetch(`${environment.apiBaseUrl}/products/config/price?${searchParams.toString()}`, {
        method: "GET",
        signal: abortSignal
      });
      return data.json();
    }
  });

  getSelectedProductConfigPrice = resource<
    ProductConfigurationPriceOverviewDto,
    | {
        productConfig: ProductConfigurationDto[];
        billingInterval: BillingIntervalInMonths;
      }
    | undefined
  >({
    request: () => {
      const config = this._selectedProducts();
      const interval = this.billingInterval();
      if (config.length === 0 || interval === undefined) {
        return undefined;
      }
      return {
        productConfig: config,
        billingInterval: interval
      };
    },
    loader: ({ request, abortSignal }) => {
      const searchParams = new URLSearchParams({
        productConfig: JSON.stringify(request?.productConfig),
        billingInterval: request?.billingInterval?.toString() ?? ""
      });

      return fetch(`${environment.apiBaseUrl}/products/config/price-total?${searchParams.toString()}`, {
        method: "GET",
        signal: abortSignal
      }).then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      });
    }
  });

  getAddOnConfigPrice = resource<
    SubscriptionAddOnPriceOverviewDto,
    | {
        addOns: RequestAddOnDto;
        billingInterval: BillingIntervalInMonths;
      }
    | undefined
  >({
    request: () => {
      const addOns = this._addOnConfig();
      const interval = this.billingInterval();
      if (
        (addOns.apiKeyCount === 0 && addOns.applicationCount === 0 && addOns.userCount === 0) ||
        interval === undefined
      ) {
        return undefined;
      }
      return {
        addOns: addOns,
        billingInterval: interval
      };
    },

    loader: async ({ request, abortSignal }) => {
      const searchParams = new URLSearchParams({
        addOns: JSON.stringify(request?.addOns),
        billingInterval: request?.billingInterval?.toString() ?? ""
      });

      const data = await fetch(`${environment.apiBaseUrl}/products/addon/price?${searchParams.toString()}`, {
        method: "GET",
        signal: abortSignal
      });
      return data.json();
    }
  });

  getSubscriptionQuote = resource<
    SubscriptionQuoteDto,
    | {
        addOns: RequestAddOnDto;
        billingInterval: BillingIntervalInMonths;
        productConfig: ProductConfigurationDto[];
      }
    | undefined
  >({
    request: () => {
      const addOns = this._addOnConfig();
      const interval = this.billingInterval();
      const config = this._selectedProducts();

      if (
        config.length === 0 ||
        (addOns.apiKeyCount === 0 && addOns.applicationCount === 0 && addOns.userCount === 0) ||
        interval === undefined
      ) {
        return undefined;
      }
      return {
        addOns: addOns,
        billingInterval: interval,
        productConfig: config
      };
    },

    loader: async ({ request, abortSignal }) => {
      const searchParams = new URLSearchParams({
        addOns: JSON.stringify(request?.addOns),
        billingInterval: request?.billingInterval?.toString() ?? "",
        productConfig: JSON.stringify(this._selectedProducts())
      });

      const data = await fetch(`${environment.apiBaseUrl}/products/subscription-quote?${searchParams.toString()}`, {
        method: "GET",
        signal: abortSignal
      });
      return data.json();
    }
  });
}
